import { useEffect } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import LockClockIcon from "@mui/icons-material/LockClock";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useForm, SubmitHandler } from "react-hook-form";
import { sendPasswordLink } from "../../redux/actions/auth.actions";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { useNavigate } from "react-router";
import FlashMessage from "../../components/Flashmessage";
import Footer from "../../Template/footer";
import { ReCaptcha } from "../../components/Captcha";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const formSchema = object({
  email: string().nonempty("Email is required").email("Email is invalid"),
  captchaToken: string().nonempty("Invalid token"),
});
type FormInput = TypeOf<typeof formSchema>;

const theme = createTheme();

const ForgetPassword: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.auth);
  const ui = useSelector((state: any) => state.UI);

  const {
    register,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
  });

  const navigate = useNavigate();

  useEffect(() => {
    register("captchaToken", { required: true });
  }, [ui, register]);

  const onSubmit: SubmitHandler<FormInput> = (data) => {
    dispatch<any>(sendPasswordLink(data, navigate));
  };

  const onVerifyCaptcha = (token: string) => {
    setValue("captchaToken", token);
  };

  if (auth.authenticated) {
    return <Navigate to="/" />;
  }
  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockClockIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Reset your password
          </Typography>

          <FlashMessage />
          <p>
            Enter your email address and we will send you a password reset link.
          </p>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ mt: 1 }}
          >
            <ReCaptcha onVerifyCaptcha={onVerifyCaptcha} />
            <TextField
              margin="normal"
              required
              fullWidth
              autoComplete="email"
              type="email"
              label="Email Address"
              error={!!errors["email"]}
              {...register("email")}
              helperText={errors["email"] ? errors["email"].message : ""}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={ui.loading}
            >
              {ui.loading ? "Sending..." : "Send Password Reset Email"}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link component={RouterLink} to="/login" variant="body2">
                  Sign In
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Footer />
      </Container>
    </ThemeProvider>
  );
};
export default ForgetPassword;
